import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Play, X } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { projects, categories } from '../data/portfolio';

const VideoModal = ({ isOpen, onClose, videoUrl, title }) => {
  if (!isOpen) return null;
  
  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.95)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '16px',
        zIndex: 999999,
      }}
      onClick={onClose}
    >
      <button
        onClick={onClose}
        style={{
          position: 'absolute',
          top: '24px',
          right: '24px',
          width: '48px',
          height: '48px',
          borderRadius: '50%',
          backgroundColor: 'rgba(255,255,255,0.1)',
          border: 'none',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          zIndex: 1000000,
        }}
      >
        <X style={{ width: '24px', height: '24px', color: 'white' }} />
      </button>
      <div
        style={{ width: '100%', maxWidth: '1200px', aspectRatio: '16/9' }}
        onClick={(e) => e.stopPropagation()}
      >
        <video
          src={videoUrl}
          autoPlay
          controls
          loop
          style={{ width: '100%', height: '100%', borderRadius: '8px', backgroundColor: '#000' }}
        />
      </div>
      <div style={{ position: 'absolute', bottom: '24px', left: '24px', color: 'white' }}>
        <p style={{ fontSize: '24px', fontWeight: 'bold', fontFamily: 'Syne, sans-serif' }}>{title}</p>
      </div>
    </div>
  );
};

const VideoCard = ({ project, index, onPlay }) => {
  const [isHovered, setIsHovered] = useState(false);
  const videoRef = useRef(null);
  
  const handleMouseEnter = () => {
    setIsHovered(true);
    if (videoRef.current && project.videoUrl) {
      videoRef.current.play().catch(() => {});
    }
  };
  
  const handleMouseLeave = () => {
    setIsHovered(false);
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
  };
  
  return (
    <motion.article
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: index * 0.1 }}
      className={`group relative ${index === 0 ? 'md:col-span-2' : ''}`}
      data-testid={`work-project-${project.id}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className={`relative overflow-hidden rounded-lg ${
        index === 0 ? 'aspect-[21/9]' : 'aspect-video'
      } bg-gray-900`}>
        {/* Video Background - Always visible, plays on hover */}
        {project.videoUrl && (
          <video
            ref={videoRef}
            src={project.videoUrl}
            muted
            loop
            playsInline
            preload="metadata"
            className="absolute inset-0 w-full h-full object-cover"
          />
        )}
        
        {/* Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent opacity-70 group-hover:opacity-90 transition-all duration-500" />
        
        {/* Category Badge */}
        <div className="absolute top-4 left-4">
          <span className="px-3 py-1.5 bg-white/10 backdrop-blur-md text-white text-xs font-medium uppercase tracking-wider rounded-full border border-white/20">
            {project.categoryLabel}
          </span>
        </div>

        {/* Play Button */}
        {project.videoUrl && (
          <motion.button
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ 
              scale: isHovered ? 1 : 0.8, 
              opacity: isHovered ? 1 : 0.7 
            }}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
            onClick={(e) => {
              e.preventDefault();
              onPlay(project);
            }}
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-16 h-16 md:w-20 md:h-20 rounded-full bg-white flex items-center justify-center shadow-2xl z-20 cursor-pointer"
          >
            <Play className="w-6 h-6 md:w-8 md:h-8 text-black fill-black ml-1" />
          </motion.button>
        )}

        {/* Arrow Link */}
        <motion.div 
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ 
            opacity: isHovered ? 1 : 0,
            scale: isHovered ? 1 : 0.8
          }}
          className="absolute top-4 right-4 z-20"
        >
          <Link 
            to={`/work/${project.id}`}
            className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center hover:bg-white/30 transition-colors"
          >
            <ArrowUpRight className="w-5 h-5 text-white" />
          </Link>
        </motion.div>

        {/* Content */}
        <div className="absolute bottom-0 left-0 right-0 p-4 md:p-6">
          <motion.div 
            initial={{ y: 20 }}
            animate={{ y: isHovered ? 0 : 10 }}
            transition={{ duration: 0.3 }}
          >
            <h3 className="text-white text-xl md:text-2xl lg:text-3xl font-bold font-['Syne'] mb-1">
              {project.title}
            </h3>
            <p className="text-white/70 text-sm md:text-base line-clamp-1">
              {project.shortDescription}
            </p>
          </motion.div>
        </div>
      </div>
      
      {/* Project Meta */}
      <div className="flex items-center justify-between text-sm text-gray-500 dark:text-gray-400 mt-3 px-1">
        <div className="flex items-center gap-3">
          <span className="font-medium">{project.client}</span>
          <span className="text-gray-300">•</span>
          <span>{project.year}</span>
        </div>
        {project.videoUrl && (
          <span className="text-xs px-2 py-1 bg-gray-100 dark:bg-gray-800 rounded">
            Video
          </span>
        )}
      </div>
    </motion.article>
  );
};

const WorkPage = () => {
  const [activeFilter, setActiveFilter] = useState('all');
  const [selectedVideo, setSelectedVideo] = useState(null);

  const filteredProjects = activeFilter === 'all'
    ? projects
    : projects.filter(p => p.category === activeFilter);

  return (
    <div className="min-h-screen bg-black text-white">
      <SEO 
        title="Our Work"
        description="Explore our portfolio of sports, automotive, lifestyle, and corporate content for leading global brands including PUMA, F1, and Chelsea FC."
        url="/work"
      />
      <Header />
      <main className="pt-20">
        {/* Page Header */}
        <section className="py-16 md:py-24 border-b border-gray-800">
          <div className="container-custom">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <p className="text-gray-400 text-sm uppercase tracking-widest mb-4">
                Our Work
              </p>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold font-['Syne'] tracking-tight mb-6">
                Creating content<br />
                <span className="text-gray-500">that moves people.</span>
              </h1>
              <p className="text-gray-400 text-lg max-w-2xl">
                From world-class athletes to luxury vehicles, we craft visual stories 
                for the brands that shape culture.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Category Filter */}
        <section className="py-6 border-b border-gray-800 sticky top-20 bg-black/95 backdrop-blur-sm z-30">
          <div className="container-custom">
            <div className="flex flex-wrap gap-2 md:gap-3">
              {categories.map((cat) => (
                <motion.button
                  key={cat.id}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setActiveFilter(cat.id)}
                  data-testid={`filter-${cat.id}`}
                  className={`px-4 md:px-6 py-2 md:py-3 rounded-full text-sm font-medium transition-all ${
                    activeFilter === cat.id
                      ? 'bg-white text-black'
                      : 'bg-gray-900 text-gray-300 hover:bg-gray-800'
                  }`}
                >
                  {cat.name}
                  {activeFilter === cat.id && (
                    <span className="ml-2 w-1.5 h-1.5 rounded-full bg-black inline-block animate-pulse" />
                  )}
                </motion.button>
              ))}
            </div>
          </div>
        </section>

        {/* Projects Grid */}
        <section className="py-12 md:py-16">
          <div className="container-custom">
            <AnimatePresence mode="wait">
              <motion.div 
                key={activeFilter}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="grid md:grid-cols-2 gap-6 lg:gap-8"
              >
                {filteredProjects.map((project, index) => (
                  <VideoCard 
                    key={project.id}
                    project={project}
                    index={index}
                    onPlay={(p) => setSelectedVideo(p)}
                  />
                ))}
              </motion.div>
            </AnimatePresence>

            {filteredProjects.length === 0 && (
              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-center py-20"
              >
                <p className="text-gray-500 text-lg">
                  No projects found in this category.
                </p>
              </motion.div>
            )}
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-16 md:py-24 border-t border-gray-800">
          <div className="container-custom">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
              {[
                { metric: '250+', label: 'Projects Delivered' },
                { metric: '45+', label: 'Global Brands' },
                { metric: '15', label: 'Countries Reached' },
                { metric: '12', label: 'Industry Awards' },
              ].map((stat, index) => (
                <motion.div
                  key={stat.label}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <p className="text-4xl md:text-5xl font-bold font-['Syne'] mb-2">
                    {stat.metric}
                  </p>
                  <p className="text-gray-500 text-sm">{stat.label}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 md:py-24 border-t border-gray-800">
          <div className="container-custom text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-4xl font-bold font-['Syne'] mb-6">
                Ready to create something<br />extraordinary?
              </h2>
              <p className="text-gray-400 text-lg mb-8 max-w-xl mx-auto">
                Let's discuss how we can bring your vision to life with world-class content.
              </p>
              <Link to="/contact">
                <motion.span
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="inline-flex items-center gap-2 px-8 py-4 bg-white text-black font-medium rounded-full"
                  data-testid="work-cta-contact"
                >
                  Start a Project
                  <ArrowUpRight className="w-5 h-5" />
                </motion.span>
              </Link>
            </motion.div>
          </div>
        </section>
      </main>
      <Footer />

      {/* Video Modal */}
      {selectedVideo && (
        <VideoModal
          isOpen={true}
          onClose={() => setSelectedVideo(null)}
          videoUrl={selectedVideo.videoUrl}
          title={selectedVideo.title}
        />
      )}
    </div>
  );
};

export default WorkPage;
